<template>
  <transition name="portal-login-fade">
    <div v-show="visible" v-if="mountedReady" class="portal-login-overlay" @click.self="onClose">
      <div class="portal-login-modal" role="dialog" aria-labelledby="portal-login-title" @click.stop>
        <button type="button" class="portal-login-modal__close" aria-label="关闭" @click="onClose">×</button>
        <div class="portal-login-modal__brand">
          <img :src="portalLogoUrl" class="portal-login-modal__logo" alt="" />
          <h2 id="portal-login-title" class="portal-login-modal__title">{{ platformName }}</h2>
        </div>

        <el-form ref="form" :model="form" :rules="rules" label-width="0" class="portal-login-modal__form" @submit.native.prevent>
          <el-form-item prop="username">
            <el-input
              v-model="form.username"
              placeholder="请输入用户名"
              maxlength="30"
              prefix-icon="el-icon-user"
              class="portal-login-modal__input"
            />
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="请输入密码"
              show-password
              prefix-icon="el-icon-lock"
              class="portal-login-modal__input"
              @keyup.enter.native="handleLogin"
            />
          </el-form-item>
          <el-form-item prop="code">
            <div class="portal-login-modal__captcha">
              <el-input v-model="form.code" placeholder="验证码计算结果" class="portal-login-modal__input captcha-input" />
              <button type="button" class="captcha-btn" title="点击刷新验证码" :disabled="captchaLoading" @click="loadCaptcha">
                <img v-if="captchaImg" class="captcha-img" :src="captchaImg" alt="验证码" />
                <span v-else class="captcha-placeholder">{{ captchaLoading ? "加载中…" : "获取验证码" }}</span>
              </button>
            </div>
          </el-form-item>
          <div class="portal-login-modal__row">
            <el-checkbox v-model="form.rememberMe">记住我</el-checkbox>
          </div>
          <el-button type="primary" class="portal-login-modal__submit" :loading="loading" @click="handleLogin">登 录</el-button>
        </el-form>
      </div>
    </div>
  </transition>
</template>

<script>
import JSEncrypt from "jsencrypt";
import { getCaptcha, login, getInfo } from "@/api/login";
import { PLATFORM_NAME, PORTAL_LOGO_URL } from "@/constants/platform";
import { closePortalLogin } from "@/utils/portalLogin";
import { canUsePortal } from "@/utils/portalAccess";

export default {
  name: "PortalLoginModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** 登录成功后跳转（空则留当前页） */
    redirect: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      platformName: PLATFORM_NAME,
      portalLogoUrl: PORTAL_LOGO_URL,
      mountedReady: false,
      form: {
        username: "",
        password: "",
        code: "",
        rememberMe: false,
        uuid: "",
      },
      captchaImg: "",
      captchaLoading: false,
      publicKeyB64: "",
      loading: false,
      rules: {
        username: [{ required: true, message: "请输入用户名", trigger: "blur" }],
        password: [{ required: true, message: "请输入密码", trigger: "blur" }],
        code: [{ required: true, message: "请输入验证码", trigger: "blur" }],
      },
    };
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadCaptcha();
        document.body.classList.add("portal-login-open");
      } else {
        document.body.classList.remove("portal-login-open");
      }
    },
  },
  mounted() {
    this.mountedReady = true;
    if (this.$el && this.$el.parentNode !== document.body) {
      document.body.appendChild(this.$el);
    }
    if (this.visible) {
      this.loadCaptcha();
      document.body.classList.add("portal-login-open");
    }
  },
  beforeDestroy() {
    document.body.classList.remove("portal-login-open");
    if (this.$el && this.$el.parentNode === document.body) {
      document.body.removeChild(this.$el);
    }
  },
  methods: {
    onClose() {
      closePortalLogin();
      this.$emit("close");
    },
    toPem(b64) {
      if (!b64) return "";
      const lines = b64.match(/.{1,64}/g).join("\n");
      return "-----BEGIN PUBLIC KEY-----\n" + lines + "\n-----END PUBLIC KEY-----";
    },
    async loadCaptcha() {
      this.captchaLoading = true;
      try {
        const res = await getCaptcha();
        const d = res.data !== undefined ? res.data : res;
        if (!d || !d.img) {
          this.captchaImg = "";
          this.$message.warning("验证码加载失败，请检查后端是否启动后点击刷新");
          return;
        }
        this.captchaImg = d.img;
        this.form.uuid = d.uuid || "";
        this.publicKeyB64 = d.publicKey || "";
        this.form.code = "";
      } catch (e) {
        this.captchaImg = "";
      } finally {
        this.captchaLoading = false;
      }
    },
    encryptPwd(pwd) {
      const enc = new JSEncrypt();
      enc.setPublicKey(this.toPem(this.publicKeyB64));
      return enc.encrypt(pwd);
    },
    handleLogin() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        this.loading = true;
        try {
          const cipher = this.encryptPwd(this.form.password);
          if (!cipher) {
            this.$message.error("密码加密失败，请刷新验证码后重试");
            await this.loadCaptcha();
            return;
          }
          const res = await login({
            username: this.form.username,
            password: cipher,
            code: this.form.code,
            uuid: this.form.uuid,
          });
          this.$store.commit("SET_TOKEN", res.token);
          const info = await getInfo();
          const u = info.data ? info.data.user : info.user;
          const rolesRaw = info.data ? info.data.roles : info.roles;
          this.$store.commit("SET_USER", u);
          this.$store.commit(
            "SET_ROLES",
            (rolesRaw || []).map((r) => r.roleKey)
          );
          const roles = this.$store.state.roles;
          if (!canUsePortal(u, roles)) {
            this.$store.commit("LOGOUT");
            this.$message.error("账号已停用或无法登录用户门户，请联系管理员");
            await this.loadCaptcha();
            return;
          }
          this.$message.success("登录成功");
          const target = this.redirect || "";
          closePortalLogin();
          this.$emit("success");
          if (target && target !== this.$route.fullPath && target !== this.$route.path) {
            this.$router.replace(target).catch(function () {});
          }
        } catch (e) {
          await this.loadCaptcha();
        } finally {
          this.loading = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
/* 挂到 body，需全局样式；参考羊驼：半透明深色遮罩，底下页面仍可见 */
body.portal-login-open {
  overflow: hidden;
}

.portal-login-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 12, 35, 0.48);
  pointer-events: auto;
}

.portal-login-modal {
  position: relative;
  width: 400px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px 28px;
  box-shadow: 0 20px 60px rgba(15, 12, 35, 0.28);
  border: none;
}

.portal-login-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 8px;
}

.portal-login-modal__close:hover {
  color: #475569;
  background: #f1f5f9;
}

.portal-login-modal__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.portal-login-modal__logo {
  width: 44px;
  height: 44px;
  margin-bottom: 10px;
  object-fit: contain;
}

.portal-login-modal__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
}

.portal-login-modal__form .el-form-item {
  margin-bottom: 16px;
}

.portal-login-modal__captcha {
  display: flex;
  gap: 10px;
  align-items: center;
}

.portal-login-modal__captcha .captcha-input {
  flex: 1;
  min-width: 0;
}

.portal-login-modal__row {
  margin-bottom: 18px;
}

.portal-login-modal__submit {
  width: 100%;
  height: 44px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 22px;
  background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
  box-shadow: 0 8px 20px rgba(22, 119, 255, 0.35);
}

.portal-login-modal__submit:hover,
.portal-login-modal__submit:focus {
  background: linear-gradient(135deg, #155bd4 0%, #1677ff 100%);
  border: none;
}

.portal-login-modal__input .el-input__inner {
  height: 44px;
  line-height: 44px;
  border-radius: 8px;
  border-color: #e2e8f0;
  font-size: 14px;
}

.portal-login-modal__input .el-input__inner:focus {
  border-color: #1677ff;
  box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.15);
}

.portal-login-modal .captcha-btn {
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 0;
  min-width: 136px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.portal-login-modal .captcha-btn:disabled {
  cursor: wait;
  opacity: 0.7;
}

.portal-login-modal .captcha-placeholder {
  display: block;
  min-width: 136px;
  height: 44px;
  line-height: 44px;
  padding: 0 10px;
  font-size: 12px;
  color: #64748b;
  text-align: center;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  background: #f8fafc;
  box-sizing: border-box;
}

.portal-login-modal .captcha-img {
  display: block;
  height: 44px;
  width: auto;
  min-width: 136px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.portal-login-fade-enter-active,
.portal-login-fade-leave-active {
  transition: opacity 0.22s ease;
}

.portal-login-fade-enter,
.portal-login-fade-leave-to {
  opacity: 0;
}
</style>
